import type {
    BorrowStatusKey,
    MinimumPriceInput,
    PoolPricingConfig,
    PoolPricingInput,
} from '@thinkalpha/ui-types/types/admin/lab-pool-pricing';
import { BORROW_STATUS_KEYS } from '@thinkalpha/ui-types/types/admin/lab-pool-pricing';

const isSameInput = <T extends PoolPricingInput>(
    a: T | undefined,
    b: T | undefined,
    valueKey: Exclude<keyof T, keyof PoolPricingInput>,
): boolean => {
    if (a === undefined || b === undefined) return a === b;
    return a.enabled === b.enabled && a[valueKey] === b[valueKey];
};

const isSameMinimum = (a: MinimumPriceInput | undefined, b: MinimumPriceInput | undefined): boolean => {
    if (a === undefined || b === undefined) return a === b;
    if (a.enabled !== b.enabled) return false;
    return BORROW_STATUS_KEYS.every((status: BorrowStatusKey) => a.prices[status] === b.prices[status]);
};

/**
 * Structural equality for pricing configs, used to compute the Info tab's
 * "unsaved changes" dirtiness and to route the simulator between the
 * saved-config and draft-config endpoints. Absent optional inputs only
 * equal absent inputs — a disabled input still differs from no input,
 * mirroring the backend's "persisted-but-disabled values are preserved"
 * contract.
 */
export function isSamePoolPricingConfig(a: PoolPricingConfig, b: PoolPricingConfig): boolean {
    if (a.hierarchy.length !== b.hierarchy.length) return false;
    if (a.hierarchy.some((method, index) => b.hierarchy[index] !== method)) return false;
    return (
        isSameMinimum(a.minimumPrice, b.minimumPrice) &&
        isSameInput(a.multiplier, b.multiplier, 'multiplier') &&
        isSameInput(a.quoteVolume, b.quoteVolume, 'percent')
    );
}
